import React, { useState, useEffect, useRef } from 'react';

export interface StateOption {
    id: number;
    name: string;
}

interface StateComboBoxProps {
    label?: string;
    value?: number;
    options: StateOption[];
    onChange: (stateId: number | undefined) => void;
    disabled?: boolean;
    readonly?: boolean;
    error?: string;
    placeholder?: string;
}

export default function StateComboBox({
    label = "State",
    value,
    options,
    onChange,
    disabled = false,
    readonly = false,
    error,
    placeholder = "Search for a state..."
}: StateComboBoxProps) {
    const selectedState = value != null ? options.find(o => o.id === value) : undefined;
    const [inputValue, setInputValue] = useState(selectedState?.name || "");
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState<StateOption[]>(options);
    const [activeDescendantIndex, setActiveDescendantIndex] = useState(-1);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Initialize input value from prop
    useEffect(() => {
        if (selectedState) {
            setInputValue(selectedState.name);
        } else {
            setInputValue("");
        }
    }, [value, selectedState]);

    // Handle options change
    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (readonly) return;
        const val = e.target.value;
        setInputValue(val);
        setIsOpen(true);
        setActiveDescendantIndex(-1);

        const filtered = options.filter(o =>
            o.name.toLowerCase().includes(val.toLowerCase())
        );
        setFilteredOptions(filtered);

        // If user clears input, notify parent
        if (val === "") {
            onChange(undefined);
        }
    };

    const handleFocus = () => {
        if (!disabled && !readonly) {
            setIsOpen(true);
        }
    };

    const handleSelect = (state: StateOption) => {
        setInputValue(state.name);
        onChange(state.id);
        setIsOpen(false);
    };

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                // Reset input to selected value on blur if no selection made
                if (selectedState) {
                    setInputValue(selectedState.name);
                } else {
                    setInputValue("");
                }
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [selectedState]);

    return (
        <div className="field state-combobox" ref={wrapperRef}>
            {label && <label>{label}</label>}
            <div className="input-wrap relative">
                <input
                    ref={inputRef}
                    className={`input ${error ? 'is-error' : ''} ${readonly ? 'is-readonly' : ''}`}
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    disabled={disabled}
                    readOnly={readonly}
                    placeholder={placeholder}
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls="state-listbox"
                    aria-activedescendant={activeDescendantIndex >= 0 && filteredOptions[activeDescendantIndex] ? `state-opt-${filteredOptions[activeDescendantIndex].id}` : undefined}
                />

                {isOpen && (
                    <ul
                        id="state-listbox"
                        className="dropdown-list"
                        role="listbox"
                    >
                        {filteredOptions.length === 0 && (
                            <li className="no-options">No states found</li>
                        )}

                        {filteredOptions.map((state, index) => (
                            <li
                                key={state.id}
                                id={`state-opt-${state.id}`}
                                className={`option ${value === state.id ? 'selected' : ''}`}
                                role="option"
                                aria-selected={value === state.id}
                                onClick={() => handleSelect(state)}
                                onMouseEnter={() => setActiveDescendantIndex(index)}
                            >
                                {state.name}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {error && <span className="error-message">{error}</span>}

            <style>{`
                .state-combobox .relative { position: relative; }
                .state-combobox .dropdown-list {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    max-height: 200px;
                    overflow-y: auto;
                    background: white;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    z-index: 10;
                    margin-top: 4px;
                    padding: 0;
                    list-style: none;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                }
                .state-combobox .option {
                    padding: 8px 12px;
                    cursor: pointer;
                    font-size: 14px;
                }
                .state-combobox .option:hover, .state-combobox .option.selected {
                    background-color: #f0f0f0;
                }
                .state-combobox .no-options {
                    padding: 12px;
                    color: #888;
                    text-align: center;
                }
                .state-combobox .is-readonly {
                    background-color: #f5f5f5;
                    cursor: default;
                    pointer-events: none;
                }
            `}</style>
        </div>
    );
}
